"use client";
import { createContext, ReactNode, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "@/context/LanguageContext";
import { GlobalServices } from "@/services/services/GlobalServices";
import Preloader from "@/components/ui/Preloader";
import { Post, Settings, User } from "@/constants/types";

type GlobalProviderProps = {
  domaines?: Post[];
  sliders?: Post[];
  settings: Settings;
  blog?: Post[];
  upcoming?: Post[];
  team?: User[];
  videos?: Post[];
};

const GlobalContext = createContext<GlobalProviderProps>({
  settings: {} as Settings,
});

export const useGlobalContext = () => {
  return useContext(GlobalContext);
};

const GlobalProvider = ({ children }: { children: ReactNode }) => {
  const { lang } = useLanguage();

  // Récupération et mise en cache des données
  const { data, isLoading } = useQuery({
    queryKey: ["homeData", lang], // Clé unique pour le cache
    queryFn: () => GlobalServices.getHomeData(lang),
    staleTime: 1000 * 60 * 5, // Cache pendant 5 minutes
  });

  const { data: domaines, isLoading: loadingDomaines } = useQuery({
    queryKey: ["domaines", lang],
    queryFn: () => GlobalServices.getPostsWithoutPagination(lang, "DOMAINE"),
    staleTime: 1000 * 60 * 10, // Cache 10 minutes
  });

  const { data: videos, isLoading: loadingVideos } = useQuery({
    queryKey: ["videos", lang],
    queryFn: () => GlobalServices.getPostsWithoutPagination(lang, "VIDEO"),
    staleTime: 1000 * 60 * 10, // Cache 10 minutes
  });

  const { data: settings, isLoading: loadingSettings } = useQuery({
    queryKey: ["settings", lang],
    queryFn: () => GlobalServices.getSettings(lang),
    staleTime: 1000 * 60 * 30, // Cache 30 minutes
  });

  return (
    <GlobalContext.Provider
      value={{
        videos: videos?.data?.data as Post[],
        domaines: domaines?.data?.data as Post[],
        sliders: data?.data?.data?.sliders,
        settings: settings?.data?.data as Settings,
        blog: data?.data?.data?.blog as Post[],
        team: data?.data?.data?.team as User[],
        upcoming: data?.data?.data?.upcoming as Post[],
      }}
    >
      {isLoading || loadingDomaines || loadingSettings || loadingVideos ? (
        <Preloader />
      ) : (
        children
      )}
    </GlobalContext.Provider>
  );
};

export default GlobalProvider;
